import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { GrowthCalendarItem, GrowthContentAsset, GrowthMarketingService } from './growth-marketing.service';
import { GrowthDistributionService } from './growth-distribution.service';
import {
  buildGrowthMarketingChannelOptions,
  growthPublishChannelLabel,
  isGrowthPublicWebPublishChannel,
  isGrowthStorefrontApplyChannel
} from './growth-channel.constants';
import {
  buildMonthGrid,
  formatMonthLabel,
  groupCalendarItemsByDay,
  monthRangeUtc
} from './growth-content-calendar-month.util';
import { CONTENT_STATUS_I18N_KEYS, normalizeContentStatus } from './growth-status.util';
import { GrowthContentReviewTaskSurfaceComponent } from './workflow-surfaces/growth-content-review-task-surface.component';

type CalendarViewMode = 'calendar' | 'list';

@Component({
  standalone: true,
  selector: 'ain-growth-content-calendar',
  providers: [MessageService],
  imports: [
    AinPageComponent,
    AinStateComponent,
    AinFormFieldComponent,
    InfoHintComponent,
    RouterLink,
    FormsModule,
    TableModule,
    ButtonModule,
    SelectModule,
    TagModule,
    DialogModule,
    ToastModule,
    AinDatePipe,
    GrowthContentReviewTaskSurfaceComponent
  ],
  template: `
    <p-toast />
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="t('growth.marketing.calendar.title')"
      [description]="t('growth.marketing.calendar.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.marketing.calendar.help.page.title'"
        [descriptionKey]="'growth.marketing.calendar.help.page.description'"
        [bulletKeys]="[
          'growth.marketing.calendar.help.page.bullets.status',
          'growth.marketing.calendar.help.page.bullets.schedule',
          'growth.marketing.calendar.help.page.bullets.publishGate'
        ]" />

      <section class="card">
        <div class="filter-grid">
          <ain-form-field [label]="t('growth.marketing.calendar.fields.channel')">
            <p-select [options]="channelOptions()" optionLabel="label" optionValue="value" [(ngModel)]="channelKey" [showClear]="true" appendTo="body" (onChange)="load()" />
          </ain-form-field>
        </div>
        <div class="toolbar">
          <div class="view-toggle">
            <button pButton type="button" [severity]="viewMode() === 'calendar' ? 'primary' : 'secondary'" [label]="t('growth.marketing.calendar.view.calendar')" (click)="setViewMode('calendar')"></button>
            <button pButton type="button" [severity]="viewMode() === 'list' ? 'primary' : 'secondary'" [label]="t('growth.marketing.calendar.view.list')" (click)="setViewMode('list')"></button>
          </div>
          @if (viewMode() === 'calendar') {
            <button pButton type="button" icon="pi pi-chevron-left" severity="secondary" (click)="shiftMonth(-1)"></button>
            <button pButton type="button" severity="secondary" [label]="t('growth.marketing.calendar.today')" (click)="goToday()"></button>
            <button pButton type="button" icon="pi pi-chevron-right" severity="secondary" (click)="shiftMonth(1)"></button>
            <span class="month-label">{{ monthLabel() }}</span>
          }
          <button pButton type="button" [label]="t('common.refresh')" severity="secondary" (click)="load()"></button>
          @if (contentPlanId()) {
            <button
              pButton
              type="button"
              severity="help"
              [label]="t('growth.marketing.calendar.generateAllBriefs')"
              [loading]="generatingAllBriefs()"
              (click)="generateAllBriefs()"></button>
          }
          <a routerLink="/growth/marketing/publish-operations" pButton severity="help" [label]="t('growth.marketing.calendar.actions.openPublishOps')"></a>
          <a routerLink="/marketplace/storefront/publishing" pButton severity="secondary" [label]="t('growth.search.seo.actions.openStorefrontPublishing')"></a>
        </div>
      </section>

      <section class="card">
        @if (loading()) {
          <ain-state type="loading" [title]="t('common.loading')" />
        } @else if (items().length === 0) {
          <ain-state type="empty" [title]="t('growth.marketing.calendar.empty')" />
        } @else if (viewMode() === 'calendar') {
          <div class="month-grid">
            @for (weekday of weekdayLabels(); track weekday) {
              <div class="weekday-head">{{ weekday }}</div>
            }
            @for (day of monthDays(); track day.key) {
              <div class="day-cell" [class.day-cell--muted]="!day.inMonth" [class.day-cell--today]="day.isToday">
                <div class="day-number">{{ day.date.getUTCDate() }}</div>
                <div class="day-items">
                  @for (row of itemsForDay(day.key); track row.id) {
                    <article class="item-chip">
                      <div class="item-chip__head">
                        <span class="item-chip__title">{{ itemTitle(row) }}</span>
                        <p-tag [value]="itemStatusLabel(row)" [severity]="itemStatusSeverity(row)" />
                      </div>
                      <small class="item-chip__meta">{{ formatScheduledTime(row.scheduledAtUtc) }} · {{ channelLabel(row.channelKey) }}</small>
                      <div class="item-chip__actions">
                        @for (action of itemActions(row); track action.key) {
                          @if (action.kind === 'review') {
                            <button pButton type="button" size="small" [label]="action.label" (click)="openReviewDialog(row)"></button>
                          } @else if (action.kind === 'submit') {
                            <button pButton type="button" size="small" [label]="action.label" (click)="submitForReview(row)"></button>
                          } @else if (action.kind === 'brief') {
                            <button pButton type="button" size="small" [label]="action.label" (click)="createBrief(row.id)"></button>
                          } @else if (action.kind === 'publishOps') {
                            <a routerLink="/growth/marketing/publish-operations" pButton size="small" severity="help" [label]="action.label"></a>
                          } @else if (action.kind === 'storefront') {
                            <button pButton type="button" size="small" severity="secondary" [label]="action.label" (click)="createStorefrontApply(row)"></button>
                          } @else if (action.kind === 'open') {
                            <a [routerLink]="['/growth/marketing/article-publishing', row.contentAssetId!]" pButton size="small" severity="secondary" [label]="action.label"></a>
                          }
                        }
                      </div>
                    </article>
                  }
                </div>
              </div>
            }
          </div>
        } @else {
          <p-table [value]="items()" [rows]="20" [paginator]="true">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.marketing.calendar.columns.contentAsset') }}</th>
                <th>{{ t('growth.marketing.calendar.columns.channel') }}</th>
                <th>{{ t('growth.strategy.activation.columns.objective') }}</th>
                <th>{{ t('growth.marketing.calendar.columns.status') }}</th>
                <th>{{ t('growth.marketing.calendar.columns.scheduledAtUtc') }}</th>
                <th>{{ t('growth.strategy.activation.columns.lineage') }}</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ itemTitle(row) }}</td>
                <td>{{ channelLabel(row.channelKey) }}</td>
                <td>{{ row.sourceObjectiveKey || '—' }}</td>
                <td>{{ itemStatusLabel(row) }}</td>
                <td>{{ row.scheduledAtUtc | ainDate:'datetime' }}</td>
                <td>{{ lineageLabel(row) }}</td>
                <td class="actions">
                  @for (action of itemActions(row); track action.key) {
                    @if (action.kind === 'review') {
                      <button pButton size="small" [label]="action.label" (click)="openReviewDialog(row)"></button>
                    } @else if (action.kind === 'submit') {
                      <button pButton size="small" [label]="action.label" (click)="submitForReview(row)"></button>
                    } @else if (action.kind === 'brief') {
                      <button pButton size="small" [label]="action.label" (click)="createBrief(row.id)"></button>
                    } @else if (action.kind === 'publishOps') {
                      <a routerLink="/growth/marketing/publish-operations" pButton size="small" severity="help" [label]="action.label"></a>
                    } @else if (action.kind === 'storefront') {
                      <button pButton size="small" severity="secondary" [label]="action.label" (click)="createStorefrontApply(row)"></button>
                    } @else if (action.kind === 'open') {
                      <a [routerLink]="['/growth/marketing/article-publishing', row.contentAssetId!]" pButton size="small" severity="secondary" [label]="action.label"></a>
                    }
                  }
                </td>
              </tr>
            </ng-template>
          </p-table>
        }
      </section>
    </ain-page>

    <p-dialog
      [header]="t('growth.marketing.calendar.reviewDialog.title')"
      [visible]="reviewDialogVisible()"
      (visibleChange)="onReviewDialogVisibleChange($event)"
      [modal]="true"
      [style]="{ width: 'min(640px, 92vw)' }"
      appendTo="body">
      @if (reviewContentAssetId()) {
        <ain-growth-content-review-task-surface
          [standaloneContentAssetId]="reviewContentAssetId()"
          (reviewCompleted)="onReviewCompleted()" />
      }
    </p-dialog>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; display: grid; gap: 10px; }
    .filter-grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .view-toggle { display: flex; gap: 6px; }
    .month-label { font-weight: 600; min-width: 160px; }
    .actions { display: flex; gap: 6px; flex-wrap: wrap; }
    .month-grid {
      display: grid;
      grid-template-columns: repeat(7, minmax(0, 1fr));
      gap: 1px;
      background: #e5e7eb;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      overflow: hidden;
    }
    .weekday-head, .day-cell { background: #fff; padding: 8px; min-height: 48px; }
    .weekday-head { font-size: 12px; font-weight: 600; color: #64748b; text-align: center; background: #f8fafc; }
    .day-cell { display: grid; grid-template-rows: auto 1fr; gap: 6px; min-height: 120px; align-content: start; }
    .day-cell--muted { background: #fafafa; color: #94a3b8; }
    .day-cell--today { box-shadow: inset 0 0 0 2px #93c5fd; }
    .day-number { font-size: 12px; font-weight: 600; color: #334155; }
    .day-items { display: grid; gap: 6px; }
    .item-chip { border: 1px solid #e5e7eb; border-radius: 8px; padding: 6px; display: grid; gap: 4px; background: #fff; }
    .item-chip__head { display: flex; gap: 4px; justify-content: space-between; align-items: flex-start; }
    .item-chip__title { font-size: 11px; font-weight: 600; line-height: 1.3; }
    .item-chip__meta { font-size: 10px; color: #64748b; }
    .item-chip__actions { display: flex; gap: 4px; flex-wrap: wrap; }
  `]
})
export class GrowthContentCalendarComponent implements OnInit {
  private readonly service = inject(GrowthMarketingService);
  private readonly distribution = inject(GrowthDistributionService);
  private readonly i18n = inject(LocalizationService);
  private readonly messages = inject(MessageService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly loading = signal(false);
  readonly generatingAllBriefs = signal(false);
  readonly items = signal<GrowthCalendarItem[]>([]);
  readonly contentPlanId = signal<string | null>(null);
  readonly viewMode = signal<CalendarViewMode>('calendar');
  readonly reviewDialogVisible = signal(false);
  readonly reviewContentAssetId = signal<string | null>(null);
  private readonly contentById = signal<Map<string, GrowthContentAsset>>(new Map());

  readonly visibleYear = signal(new Date().getUTCFullYear());
  readonly visibleMonth = signal(new Date().getUTCMonth());

  channelKey: string | null = null;

  readonly monthDays = computed(() => buildMonthGrid(this.visibleYear(), this.visibleMonth()));
  readonly itemsByDay = computed(() => groupCalendarItemsByDay(this.items()));
  readonly monthLabel = computed(() => formatMonthLabel(
    this.visibleYear(),
    this.visibleMonth(),
    this.i18n.activeCulture()
  ));

  constructor() {
    this.route.queryParamMap.pipe(takeUntilDestroyed()).subscribe(params => {
      const view = params.get('view');
      if (view === 'list' || view === 'calendar') {
        this.viewMode.set(view);
      }
      const channel = params.get('channel');
      if (channel !== this.channelKey) {
        this.channelKey = channel;
        this.load();
      }
    });
  }

  ngOnInit(): void {
    this.service.listContent().subscribe({
      next: rows => {
        this.contentById.set(new Map(rows.map(row => [row.id, row])));
        this.load();
      },
      error: () => this.load()
    });
  }

  weekdayLabels(): string[] {
    const formatter = new Intl.DateTimeFormat(this.i18n.activeCulture(), { weekday: 'short', timeZone: 'UTC' });
    return Array.from({ length: 7 }, (_, index) => formatter.format(new Date(Date.UTC(2024, 0, 7 + index))));
  }

  load(): void {
    const { fromUtc, toUtc } = this.viewMode() === 'calendar'
      ? monthRangeUtc(this.visibleYear(), this.visibleMonth())
      : this.listRangeUtc();

    this.loading.set(true);
    this.service.getCalendar(fromUtc, toUtc, this.channelKey ?? undefined).subscribe({
      next: rows => {
        this.items.set(rows);
        this.contentPlanId.set(this.resolveContentPlanId(rows));
        this.loading.set(false);
      },
      error: () => this.loading.set(false)
    });
  }

  setViewMode(mode: CalendarViewMode): void {
    this.viewMode.set(mode);
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { view: mode },
      queryParamsHandling: 'merge'
    });
    this.load();
  }

  shiftMonth(delta: number): void {
    const date = new Date(Date.UTC(this.visibleYear(), this.visibleMonth() + delta, 1));
    this.visibleYear.set(date.getUTCFullYear());
    this.visibleMonth.set(date.getUTCMonth());
    this.load();
  }

  goToday(): void {
    const now = new Date();
    this.visibleYear.set(now.getUTCFullYear());
    this.visibleMonth.set(now.getUTCMonth());
    this.load();
  }

  itemsForDay(dayKey: string): GrowthCalendarItem[] {
    return this.itemsByDay().get(dayKey) ?? [];
  }

  channelOptions(): Array<{ label: string; value: string }> {
    return buildGrowthMarketingChannelOptions(key => this.t(key));
  }

  channelLabel(channelKey: string): string {
    return growthPublishChannelLabel(channelKey, key => this.t(key));
  }

  itemTitle(row: GrowthCalendarItem): string {
    if (row.contentAssetId) {
      return this.contentLabel(row.contentAssetId);
    }
    return row.title || '—';
  }

  itemStatusLabel(row: GrowthCalendarItem): string {
    const asset = row.contentAssetId ? this.contentById().get(row.contentAssetId) : null;
    if (asset) {
      return this.t(CONTENT_STATUS_I18N_KEYS[normalizeContentStatus(asset.status)]);
    }
    return row.status;
  }

  itemStatusSeverity(row: GrowthCalendarItem): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' | undefined {
    const asset = row.contentAssetId ? this.contentById().get(row.contentAssetId) : null;
    const status = asset ? normalizeContentStatus(asset.status) : row.status;
    switch (status) {
      case 'Published':
      case 'Approved':
        return 'success';
      case 'InReview':
      case 'Scheduled':
        return 'info';
      case 'Rejected':
        return 'danger';
      case 'Draft':
      case 'AiGenerated':
        return 'warn';
      default:
        return 'secondary';
    }
  }

  itemActions(row: GrowthCalendarItem): Array<{ key: string; kind: string; label: string }> {
    const actions: Array<{ key: string; kind: string; label: string }> = [];
    const asset = row.contentAssetId ? this.contentById().get(row.contentAssetId) : null;
    const contentStatus = asset ? normalizeContentStatus(asset.status) : null;

    if (!row.briefId) {
      actions.push({ key: 'brief', kind: 'brief', label: this.t('growth.strategy.activation.calendar.createBrief') });
    }

    if (contentStatus === 'Draft' || contentStatus === 'AiGenerated' || contentStatus === 'Rejected') {
      actions.push({ key: 'submit', kind: 'submit', label: this.t('growth.marketing.campaigns.actions.submitForReview') });
    }

    if (contentStatus === 'InReview' && row.contentAssetId) {
      actions.push({ key: 'review', kind: 'review', label: this.t('growth.articlePublishing.reviewList.actions.openReview') });
    }

    if (contentStatus === 'Approved' || contentStatus === 'Scheduled' || contentStatus === 'Published') {
      if (row.contentAssetId) {
        actions.push({ key: 'open', kind: 'open', label: this.t('growth.marketing.calendar.actions.openContent') });
      }
    }

    if (isGrowthPublicWebPublishChannel(row.channelKey)) {
      actions.push({ key: 'publishOps', kind: 'publishOps', label: this.t('growth.marketing.calendar.actions.openPublishOps') });
    }

    if (isGrowthStorefrontApplyChannel(row.channelKey) && row.contentAssetId) {
      actions.push({ key: 'storefront', kind: 'storefront', label: this.t('growth.marketing.calendar.actions.createStorefrontApply') });
    }

    return actions;
  }

  contentLabel(contentAssetId: string): string {
    const asset = this.contentById().get(contentAssetId);
    return asset ? `${asset.title} (${asset.contentType})` : contentAssetId;
  }

  lineageLabel(row: GrowthCalendarItem): string {
    if (row.activationPlanId) return this.t('growth.strategy.activation.columns.fromPlan');
    if (row.strategyArtifactId) return this.t('growth.strategy.activation.columns.fromArtifact');
    return '—';
  }

  openReviewDialog(row: GrowthCalendarItem): void {
    if (!row.contentAssetId) {
      return;
    }
    this.reviewContentAssetId.set(row.contentAssetId);
    this.reviewDialogVisible.set(true);
  }

  onReviewDialogVisibleChange(visible: boolean): void {
    this.reviewDialogVisible.set(visible);
    if (!visible) {
      this.reviewContentAssetId.set(null);
    }
  }

  onReviewCompleted(): void {
    this.reviewDialogVisible.set(false);
    this.reviewContentAssetId.set(null);
    this.refreshContentMap();
    this.load();
  }

  submitForReview(row: GrowthCalendarItem): void {
    if (!row.contentAssetId) {
      return;
    }
    this.service.submitContentForReview(row.contentAssetId).subscribe({
      next: () => this.refreshContentMap()
    });
  }

  createBrief(itemId: string): void {
    this.distribution.createBriefFromCalendarItem(itemId, {
      idempotencyKey: `brief-${itemId}-${Date.now()}`,
      correlationId: crypto.randomUUID()
    }).subscribe(() => this.load());
  }

  generateAllBriefs(): void {
    const planId = this.contentPlanId();
    if (!planId || this.generatingAllBriefs()) {
      return;
    }

    this.generatingAllBriefs.set(true);
    this.distribution.generateAllBriefs(planId).subscribe({
      next: result => {
        this.generatingAllBriefs.set(false);
        this.messages.add({
          severity: 'success',
          summary: this.t('growth.marketing.calendar.briefsGenerated').replace('{count}', String(result.created))
        });
        this.load();
      },
      error: () => this.generatingAllBriefs.set(false)
    });
  }

  createStorefrontApply(row: GrowthCalendarItem): void {
    if (!row.contentAssetId) return;
    const targetType = row.channelKey.startsWith('storefront-plp') || row.channelKey === 'marketplace-plp'
      ? 'storefront-plp'
      : 'storefront-pdp';
    this.service.ensureContentReadyForStorefrontApply(row.contentAssetId).subscribe({
      next: asset => {
        this.distribution.createStorefrontApplyRequest({
          contentAssetId: asset.id,
          targetType,
          offeringRefId: null,
          seoProfileId: null,
          requestPayloadJson: '{}',
          idempotencyKey: `calendar-sf-${row.id}-${Date.now()}`,
          correlationId: crypto.randomUUID(),
          activationPlanId: row.activationPlanId ?? null,
          strategyArtifactId: row.strategyArtifactId ?? null
        }).subscribe();
      }
    });
  }

  private refreshContentMap(): void {
    this.service.listContent().subscribe({
      next: rows => this.contentById.set(new Map(rows.map(row => [row.id, row])))
    });
  }

  private listRangeUtc(): { fromUtc: string; toUtc: string } {
    const now = Date.now();
    return {
      fromUtc: new Date(now - 30 * 86400000).toISOString(),
      toUtc: new Date(now + 90 * 86400000).toISOString()
    };
  }

  private resolveContentPlanId(rows: GrowthCalendarItem[]): string | null {
    const planIds = [...new Set(rows.map(row => row.planId).filter(Boolean))];
    return planIds.length === 1 ? planIds[0] : null;
  }

  formatScheduledTime(iso: string): string {
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) {
      return '—';
    }
    return `${String(parsed.getUTCHours()).padStart(2, '0')}:${String(parsed.getUTCMinutes()).padStart(2, '0')} UTC`;
  }

  t(key: string): string {
    return this.i18n.translate(key);
  }
}
